import { THEMES } from "./themes";

export type ThemeSection = { h2: string; paragraphs: string[] };

export type ThemePage = {
  slug: string;
  name: string;
  metaTitle: string;
  metaDescription: string;
  h1: string;
  eyebrow: string;
  intro: string;
  hero: string;
  pillars: { title: string; text: string }[];
  sections: ThemeSection[];
  /** Fiche matériel liée (slug de /location-materiel/$slug). */
  equipment: { slug: string; label: string };
  faq: { q: string; a: string }[];
};

const heroOf = (slug: string) =>
  THEMES.find((t) => t.slug === slug)?.image ?? THEMES[0].image;

export const THEME_PAGES: Record<string, ThemePage> = {
  "tempete-de-neige": {
    slug: "tempete-de-neige",
    name: "Tempête de neige",
    metaTitle: "Soirée tempête de neige : DJ & machine à neige | DJ Val",
    metaDescription:
      "Soirée tempête de neige en Vaucluse : machine à neige artificielle, DJ et jeux de lumière pour un mariage d'hiver ou un événement féerique. Devis gratuit sous 24 h.",
    h1: "Soirée tempête de neige : l'effet hiver dans tout le Vaucluse",
    eyebrow: "Soirées thématiques · Effet neige",
    intro:
      "Des flocons qui tombent sur la piste au moment exact du premier slow : la soirée tempête de neige transforme une salle ordinaire en décor de conte. Installation, réglage et pilotage assurés par DJ Val à Cavaillon, Avignon et dans tout le Vaucluse.",
    hero: heroOf("tempete-de-neige"),
    pillars: [
      {
        title: "Machine à neige professionnelle",
        text: "Machines à neige artificielle à haut débit, alimentées par un liquide non gras, sans résidu ni tache sur les tenues et le parquet. Débit et hauteur de projection réglés selon la salle.",
      },
      {
        title: "Synchronisation avec le mix",
        text: "L'effet neige est déclenché à la seconde près sur un moment fort : entrée des mariés, premier slow, dernier morceau. Rien n'est laissé au hasard ni au minuteur.",
      },
      {
        title: "Installation et sécurité",
        text: "Machines fixées en hauteur ou sur pied, câblage sécurisé, contrôle du sol pour éviter toute glissade. Un test complet est effectué avant l'arrivée de vos invités.",
      },
    ],
    sections: [
      {
        h2: "Pourquoi choisir une soirée tempête de neige",
        paragraphs: [
          "La tempête de neige est l'un des effets les plus photogéniques d'une soirée. Contrairement aux fumées lourdes ou aux lasers, elle produit une émotion douce : les invités lèvent la tête, tendent la main, sortent leur téléphone. C'est le moment que l'on retrouve systématiquement dans les vidéos de fin de soirée.",
          "Elle fonctionne particulièrement bien pour les mariages d'hiver, les fêtes de fin d'année, les soirées de Noël d'entreprise, les anniversaires à thème nordique et les baptêmes. En Provence, où la vraie neige est rare, l'effet surprend d'autant plus les invités : dans une bastide du Luberon ou une salle des fêtes du Vaucluse, la piste se couvre de flocons en quelques secondes.",
          "L'effet reste maîtrisé : la neige artificielle est composée d'eau et d'un agent moussant biodégradable. Elle s'évapore en séchant et ne laisse ni film gras ni auréole. Sur un sol lisse, un tapis antidérapant est posé en périphérie de la zone d'effet.",
        ],
      },
      {
        h2: "Comment se déroule une soirée neige avec DJ Val",
        paragraphs: [
          "Tout commence par un échange sur la salle : hauteur sous plafond, type de sol, présence d'un détecteur de fumée, emplacement de la piste. Ces quatre éléments déterminent le nombre de machines et leur position. Une salle de 150 personnes demande généralement deux machines placées en diagonale pour couvrir toute la piste.",
          "Le jour J, l'installation est réalisée en amont de l'accueil des invités, en même temps que la sono et l'éclairage. Un test à faible débit permet de vérifier la portée, l'orientation et l'absence de gêne pour le buffet ou les tables.",
          "Pendant la soirée, l'effet est déclenché manuellement depuis la régie, jamais en continu. Deux à quatre séquences d'une à deux minutes suffisent à créer la surprise ; au-delà, l'effet perd sa magie. La lumière est adaptée en parallèle : un wash blanc froid ou bleuté fait briller les flocons, alors qu'une lumière chaude les rend invisibles.",
        ],
      },
      {
        h2: "Combiner la neige avec les autres effets",
        paragraphs: [
          "La tempête de neige se marie très bien avec un ciel étoilé, des guirlandes guinguette ou un éclairage architectural en bleu glacier. En revanche, on évite de l'associer à un canon à confettis dans la même séquence : les deux effets se neutralisent visuellement.",
          "Pour un mariage, l'enchaînement le plus efficace reste : entrée des mariés sous les étincelles froides, premier slow sous la neige, puis ouverture du dancefloor avec les lumières mobiles. Chaque effet occupe son moment et garde son impact.",
          "Toutes les prestations incluent la sonorisation, l'éclairage, l'animation DJ et le pilotage des effets. Vous n'avez ni matériel à louer séparément, ni technicien supplémentaire à prévoir.",
        ],
      },
    ],
    equipment: { slug: "machine-fumee-lourde", label: "Location de machine à fumée lourde" },
    faq: [
      {
        q: "La neige artificielle tache-t-elle les vêtements ?",
        a: "Non. Le liquide utilisé est à base d'eau et d'un agent moussant biodégradable, sans huile ni colorant. Il s'évapore en séchant et ne laisse aucune trace sur les robes, costumes ou tapis.",
      },
      {
        q: "Le sol devient-il glissant ?",
        a: "Sur un parquet ou un carrelage lisse, une fine humidité peut apparaître après plusieurs minutes d'effet. C'est pourquoi les séquences sont courtes, le débit réglé et un tapis antidérapant posé si nécessaire.",
      },
      {
        q: "Peut-on faire une tempête de neige en extérieur ?",
        a: "Oui, à condition qu'il n'y ait pas de vent fort. Au-delà de 15 km/h, les flocons sont dispersés avant d'atteindre la piste. Une zone abritée ou une tente est alors préférable.",
      },
      {
        q: "L'effet déclenche-t-il les détecteurs de fumée ?",
        a: "La neige artificielle n'est pas une fumée et ne déclenche pas les détecteurs optiques dans la grande majorité des salles. Nous vérifions systématiquement le type de détection avec le responsable du lieu avant l'événement.",
      },
      {
        q: "Combien coûte une soirée tempête de neige en Vaucluse ?",
        a: "L'effet neige est proposé en option d'une prestation DJ, à partir de 150 € pour une machine installée et pilotée sur la soirée. Le devis exact dépend de la salle et du nombre de machines. Réponse sous 24 h au 07 67 90 86 67.",
      },
    ],
  },

  "tempete-de-bulles": {
    slug: "tempete-de-bulles",
    name: "Tempête de bulles",
    metaTitle: "Soirée tempête de bulles : DJ & machine à bulles | DJ Val",
    metaDescription:
      "Soirée tempête de bulles en Vaucluse : machine à bulles XXL, DJ et lumières pour mariage, baptême ou anniversaire. Effet féerique garanti, devis gratuit sous 24 h.",
    h1: "Soirée tempête de bulles : l'effet féerique pour vos événements",
    eyebrow: "Soirées thématiques · Effet bulles",
    intro:
      "Des milliers de bulles qui flottent au-dessus de la piste, captent la lumière et font courir les enfants : la tempête de bulles est l'effet le plus doux et le plus universel du catalogue. Installée et pilotée par DJ Val à Cavaillon, Avignon et dans tout le Vaucluse.",
    hero: heroOf("tempete-de-bulles"),
    pillars: [
      {
        title: "Machines à bulles XXL",
        text: "Machines à haut rendement produisant plusieurs milliers de bulles par minute, réparties sur toute la largeur de la piste plutôt qu'en un seul point.",
      },
      {
        title: "Liquide non irritant",
        text: "Liquide professionnel sans parfum ni colorant, adapté à la présence d'enfants. Il sèche sans coller et ne laisse pas de film sur les sols.",
      },
      {
        title: "Effet piloté en direct",
        text: "Les séquences sont lancées depuis la régie sur les moments choisis : ouverture de bal, arrivée du gâteau, animation enfants en début de soirée.",
      },
    ],
    sections: [
      {
        h2: "À quels événements convient la tempête de bulles",
        paragraphs: [
          "La tempête de bulles est le seul effet qui plaît simultanément aux enfants, aux grands-parents et aux mariés. Elle ne fait pas de bruit, n'aveugle personne et n'exige aucune préparation des invités. C'est pour cette raison qu'elle est autant demandée sur les baptêmes, communions, anniversaires d'enfants et vins d'honneur de mariage.",
          "Sur un mariage, elle est souvent utilisée deux fois : une première séquence pendant le vin d'honneur, quand les enfants tournent autour des tables, puis une seconde pendant l'ouverture de bal pour donner du relief aux photos. Les photographes l'apprécient particulièrement : les bulles captent les sources lumineuses et créent des points de lumière naturels dans le cadre.",
          "Elle fonctionne aussi très bien en extérieur, dans les cours de bastide et les jardins de domaines du Luberon, à condition que le vent reste léger.",
        ],
      },
      {
        h2: "Installation, réglages et bonnes pratiques",
        paragraphs: [
          "La machine est placée en hauteur, sur pied ou fixée à une structure, orientée légèrement vers le haut afin que les bulles descendent doucement sur la piste plutôt que de la mouiller directement. Cette orientation est le détail qui fait toute la différence entre un effet réussi et un sol savonneux.",
          "Le débit est réglé en fonction du volume de la salle. Dans une salle des fêtes de 200 m², une machine bien placée suffit ; dans un espace ouvert ou en extérieur, deux machines réparties évitent que l'effet ne se concentre d'un seul côté.",
          "Comme pour la neige, les séquences restent courtes : une à deux minutes, deux à quatre fois dans la soirée. Une tempête de bulles en continu finit par saturer l'air et rendre le sol glissant.",
        ],
      },
      {
        h2: "Louer la machine seule ou avec le DJ",
        paragraphs: [
          "Si vous organisez vous-même votre événement, la machine à bulles est disponible en location seule, avec le liquide, le pied et la notice de réglage. Le retrait s'effectue gratuitement à Cavaillon, la livraison est possible dans tout le Vaucluse.",
          "Si vous confiez l'animation à DJ Val, l'effet est inclus dans la prestation : installation, liquide, pilotage et démontage. Vous n'avez rien à manipuler pendant la soirée.",
          "Les deux formules peuvent être combinées avec d'autres effets — confettis, fluo, CO₂ — pour construire une soirée signature en plusieurs temps forts.",
        ],
      },
    ],
    equipment: { slug: "machine-bulles", label: "Location de machine à bulles" },
    faq: [
      {
        q: "Le liquide à bulles est-il dangereux pour les enfants ?",
        a: "Non. Nous utilisons un liquide professionnel sans parfum, sans colorant et non irritant, spécifiquement adapté aux événements familiaux. Il reste toutefois déconseillé d'en ingérer, comme tout produit de ce type.",
      },
      {
        q: "Le sol devient-il glissant avec les bulles ?",
        a: "Pas si la machine est correctement orientée vers le haut et les séquences limitées à quelques minutes. C'est un réglage que nous faisons systématiquement à l'installation, en tenant compte du revêtement de la piste.",
      },
      {
        q: "Peut-on utiliser la machine à bulles en extérieur ?",
        a: "Oui, c'est même l'un des meilleurs contextes : cour de bastide, jardin, terrasse. Il faut simplement un vent faible, sinon les bulles sont emportées avant d'atteindre les invités.",
      },
      {
        q: "Combien de temps dure une recharge de liquide ?",
        a: "Un bidon de 5 litres permet environ 4 à 5 heures d'utilisation en séquences. Sur une prestation DJ Val, le liquide nécessaire est toujours inclus, avec une réserve de secours.",
      },
      {
        q: "Peut-on combiner bulles et confettis dans la même soirée ?",
        a: "Oui, à condition de les séparer dans le temps : les bulles pendant le vin d'honneur ou l'ouverture de bal, les confettis sur un temps fort du dancefloor. Les deux effets ne doivent pas se chevaucher.",
      },
    ],
  },

  fluo: {
    slug: "fluo",
    name: "Soirée fluo",
    metaTitle: "Soirée fluo DJ : lumière noire UV & néons | DJ Val",
    metaDescription:
      "Organisez une soirée fluo avec DJ, lumière noire UV, peintures et accessoires néon en Vaucluse. Anniversaire, soirée étudiante ou association : devis gratuit sous 24 h.",
    h1: "Soirée fluo avec DJ : lumière noire, néons et dancefloor UV",
    eyebrow: "Soirées thématiques · Fluo & UV",
    intro:
      "Lumière noire, peintures fluo, bracelets et t-shirts qui s'allument dans le noir : la soirée fluo est l'animation la plus énergique du catalogue. DJ, sono et matériel UV installés par DJ Val à Cavaillon, Avignon et dans tout le Vaucluse.",
    hero: heroOf("fluo"),
    pillars: [
      {
        title: "Lumière noire haute puissance",
        text: "Projecteurs UV LED répartis autour de la piste pour faire réagir peintures, vêtements blancs et accessoires fluo sur toute la surface, pas seulement au centre.",
      },
      {
        title: "Kit fluo pour les invités",
        text: "Peintures corporelles hypoallergéniques, bracelets lumineux, colliers et accessoires distribués à l'arrivée : chacun devient un élément du décor.",
      },
      {
        title: "Programmation musicale adaptée",
        text: "Un set fluo se construit autour de l'électro, du dance et des hits fédérateurs, avec une montée d'intensité progressive et des temps forts marqués.",
      },
    ],
    sections: [
      {
        h2: "Ce qui fait une vraie soirée fluo",
        paragraphs: [
          "Une soirée fluo réussie ne se résume pas à brancher deux projecteurs UV. L'essentiel se joue sur trois points : la quantité de lumière noire, l'obscurité de la salle et la participation des invités. Si la salle reste éclairée par un plafonnier ou par la lumière du jour, l'effet UV disparaît complètement.",
          "Nous prévoyons donc systématiquement un repérage : occultation des ouvertures, extinction des éclairages parasites, et répartition des projecteurs UV en périphérie plutôt qu'en un seul point. Une piste de 100 m² demande en général quatre à six projecteurs pour que chaque invité réagisse à la lumière, quel que soit son emplacement.",
          "La participation compte autant que le matériel : les invités sont prévenus en amont de venir en blanc ou en couleurs claires, et un stand de peinture fluo est installé à l'entrée. En dix minutes, la salle entière est transformée.",
        ],
      },
      {
        h2: "Pour quels événements",
        paragraphs: [
          "La soirée fluo est très demandée pour les anniversaires de 16 à 30 ans, les soirées étudiantes, les fêtes d'associations et de comités d'entreprise, les EVJF et EVG, ainsi que les fins de soirée de mariage lorsque les mariés veulent un second souffle après minuit.",
          "Sur un mariage, le format le plus efficace consiste à basculer en mode fluo après le gâteau : les accessoires sont distribués, les projecteurs UV s'allument et la soirée repart pour deux heures. C'est une animation qui relance le dancefloor sans changer de lieu ni de prestataire.",
          "Pour les collectivités et les associations du Vaucluse, la formule est proposée clé en main : sono, lumière noire, kit fluo et animation, avec une déclaration SACEM et une assurance responsabilité civile professionnelle en règle.",
        ],
      },
      {
        h2: "Matériel, sécurité et nettoyage",
        paragraphs: [
          "Les projecteurs utilisés sont des UV LED de dernière génération : ils ne chauffent pas, consomment peu et ne présentent pas les risques des anciens tubes à vapeur de mercure. Ils sont installés hors de portée des invités et fixés sur pieds lestés.",
          "Les peintures corporelles sont hypoallergéniques, à base d'eau, et s'éliminent au savon. Nous prévoyons des lingettes et un point d'eau signalé à proximité. Pour préserver la salle, une zone de peinture délimitée est mise en place à l'écart des tables et du buffet.",
          "Le matériel UV peut également être loué seul si vous gérez vous-même l'animation, avec retrait gratuit à Cavaillon et livraison possible dans le Vaucluse.",
        ],
      },
    ],
    equipment: { slug: "lumiere-noire-uv", label: "Location de lumière noire UV" },
    faq: [
      {
        q: "Faut-il une salle totalement noire pour une soirée fluo ?",
        a: "Il faut une salle que l'on peut obscurcir : volets, rideaux ou soirée après la tombée de la nuit. La lumière du jour et les plafonniers annulent l'effet des projecteurs UV.",
      },
      {
        q: "Les peintures fluo partent-elles facilement ?",
        a: "Oui, ce sont des peintures corporelles à base d'eau qui s'éliminent au savon. Sur les vêtements, un lavage classique suffit dans la très grande majorité des cas.",
      },
      {
        q: "Quelle tenue conseiller aux invités ?",
        a: "Le blanc et les couleurs claires réagissent le mieux sous lumière noire. Un simple t-shirt blanc suffit à obtenir un effet spectaculaire, sans acheter de tenue spécifique.",
      },
      {
        q: "La soirée fluo convient-elle aux enfants ?",
        a: "Oui, sous surveillance. Les projecteurs LED UV ne chauffent pas et les peintures sont hypoallergéniques. Nous adaptons le volume sonore et la programmation pour les événements familiaux.",
      },
      {
        q: "Peut-on louer uniquement le matériel de lumière noire ?",
        a: "Oui. Les projecteurs UV et les accessoires fluo sont disponibles en location seule, avec retrait gratuit à Cavaillon ou livraison dans le Vaucluse. Renseignements au 07 67 90 86 67.",
      },
    ],
  },

  confettis: {
    slug: "confettis",
    name: "Soirée confettis",
    metaTitle: "Soirée confettis : canon à confettis & DJ | DJ Val",
    metaDescription:
      "Soirée confettis en Vaucluse : canon à confettis biodégradables synchronisé avec le DJ pour mariage, anniversaire ou soirée d'entreprise. Devis gratuit sous 24 h.",
    h1: "Soirée confettis : le canon qui marque le temps fort de votre soirée",
    eyebrow: "Soirées thématiques · Confettis",
    intro:
      "Une explosion de couleurs déclenchée à la seconde près sur le refrain : le canon à confettis est l'effet le plus photographié d'une soirée. Installé, synchronisé et nettoyé par DJ Val à Cavaillon, Avignon et dans tout le Vaucluse.",
    hero: heroOf("confettis"),
    pillars: [
      {
        title: "Confettis biodégradables",
        text: "Papier ignifugé et biodégradable, en blanc, or ou multicolore selon votre décoration. Aucun plastique, aucun résidu métallique sur les sols et les tables.",
      },
      {
        title: "Déclenchement synchronisé",
        text: "Le tir est déclenché depuis la régie, calé sur le drop ou le refrain choisi avec vous. Un tir bien placé vaut trois tirs au hasard.",
      },
      {
        title: "Nettoyage inclus",
        text: "Le ramassage après la soirée fait partie de la prestation. C'est souvent le point oublié des locations sans DJ, et la première cause de litige avec les salles.",
      },
    ],
    sections: [
      {
        h2: "Quand déclencher les confettis",
        paragraphs: [
          "Le canon à confettis fonctionne par contraste : il doit surprendre. Sur un mariage, les deux moments les plus efficaces sont l'entrée des mariés dans la salle et la fin de l'ouverture de bal, quand les invités rejoignent la piste. Sur une soirée d'entreprise ou un anniversaire, on privilégie le morceau signature de la soirée, souvent placé vers minuit.",
          "Un tir unique bien préparé produit plus d'effet que quatre tirs dispersés. C'est pour cette raison que nous calons systématiquement le moment avec vous en amont, plutôt que d'improviser pendant la soirée.",
          "L'effet se combine parfaitement avec le canon CO₂ ou les étincelles froides : les confettis restent en suspension quelques secondes, ce qui laisse le temps aux photographes de capter la scène.",
        ],
      },
      {
        h2: "Matériel et contraintes de salle",
        paragraphs: [
          "Nous utilisons des canons électriques rechargeables et, selon les configurations, des machines à confettis à turbine pour une diffusion continue et plus douce. Le choix dépend de la hauteur sous plafond : en dessous de trois mètres, la turbine est préférable, le canon envoyant les confettis trop haut.",
          "Certaines salles du Vaucluse interdisent les confettis ou facturent un supplément de nettoyage. Nous vérifions ce point avec le lieu avant l'événement, et proposons si besoin de concentrer l'effet sur une zone délimitée de la piste.",
          "Les confettis utilisés sont ignifugés, conformes aux normes en vigueur pour les établissements recevant du public, et biodégradables. Les confettis métallisés, interdits dans de nombreuses salles, ne font pas partie de notre matériel.",
        ],
      },
      {
        h2: "Prestation DJ ou location seule",
        paragraphs: [
          "En prestation complète, l'effet confettis est intégré au déroulé de la soirée : réglage, chargement, tir et ramassage inclus. Vous choisissez la couleur et le moment, nous gérons le reste.",
          "En location seule, la machine à confettis est disponible avec ses recharges et sa télécommande, avec retrait gratuit à Cavaillon ou livraison dans le Vaucluse. Un mode d'emploi et un rappel des règles de sécurité vous sont remis à la prise en main.",
          "Dans les deux cas, un devis clair est transmis sous 24 heures, sans frais cachés de nettoyage ou de recharge.",
        ],
      },
    ],
    equipment: { slug: "machine-confettis", label: "Location de machine à confettis" },
    faq: [
      {
        q: "Les confettis sont-ils autorisés dans toutes les salles ?",
        a: "Non, certaines salles les interdisent ou facturent un forfait nettoyage. Nous contactons le lieu en amont pour vérifier et, si besoin, adapter l'effet à une zone délimitée.",
      },
      {
        q: "Les confettis sont-ils biodégradables ?",
        a: "Oui, nous n'utilisons que du papier ignifugé et biodégradable, sans plastique ni métallisation. C'est aussi la condition posée par la plupart des domaines et bastides de Provence.",
      },
      {
        q: "Qui nettoie après la soirée ?",
        a: "Le ramassage est inclus dans nos prestations DJ. En location seule, le nettoyage reste à votre charge, comme le prévoient nos conditions de location.",
      },
      {
        q: "Combien de tirs prévoir sur une soirée ?",
        a: "Un à deux tirs suffisent dans la majorité des cas. Au-delà, l'effet de surprise disparaît et la quantité de confettis au sol devient problématique pour la salle.",
      },
      {
        q: "Peut-on choisir la couleur des confettis ?",
        a: "Oui : blanc, or, argent ou multicolore, selon votre décoration. Le choix est à confirmer une dizaine de jours avant l'événement pour garantir la disponibilité.",
      },
    ],
  },

  co2: {
    slug: "co2",
    name: "Soirée canon CO₂",
    metaTitle: "Soirée canon CO2 : effet jet de fumée glacée | DJ Val",
    metaDescription:
      "Soirée canon CO2 en Vaucluse : jets de fumée glacée synchronisés avec le DJ pour mariage, club ou soirée d'entreprise. Effet stadium garanti, devis gratuit sous 24 h.",
    h1: "Soirée canon CO2 : l'effet stadium sur votre dancefloor",
    eyebrow: "Soirées thématiques · Canon CO₂",
    intro:
      "Des jets de fumée glacée à -78 °C qui montent au plafond sur le drop : le canon CO₂ est l'effet le plus spectaculaire du catalogue. Bouteilles, flexibles et pilotage assurés par DJ Val à Cavaillon, Avignon et dans tout le Vaucluse.",
    hero: heroOf("co2"),
    pillars: [
      {
        title: "Canons professionnels",
        text: "Canons CO₂ alimentés par bouteilles à tube plongeur, avec flexibles haute pression contrôlés. Le même matériel que celui utilisé en club et en festival.",
      },
      {
        title: "Tir calé sur le mix",
        text: "Le jet est déclenché sur le drop, jamais au hasard. Cette synchronisation est ce qui distingue un effet de club d'un simple nuage de fumée.",
      },
      {
        title: "Sécurité maîtrisée",
        text: "Bouteilles arrimées, zone de tir dégagée, orientation vers le haut et distance minimale respectée avec le public. Salle ventilée vérifiée avant l'événement.",
      },
    ],
    sections: [
      {
        h2: "Ce que produit réellement un canon CO₂",
        paragraphs: [
          "Le canon CO₂ projette un jet de gaz carbonique détendu qui se condense instantanément en un panache blanc très dense, extrêmement froid et parfaitement dessiné. Contrairement à une machine à fumée, l'effet est vertical, net et disparaît en quelques secondes : il ne sature jamais la salle et ne gêne pas la visibilité.",
          "C'est l'effet le plus proche de l'expérience club ou festival. Sur un dancefloor, un tir de deux secondes synchronisé avec un drop provoque systématiquement une réaction collective. Il fonctionne aussi en journée, sur une terrasse ou un espace ouvert, là où lasers et lumières perdent tout impact.",
          "Le froid intense du jet — environ -78 °C à la sortie — crée également une sensation physique appréciée lors des soirées d'été, sans humidifier la piste.",
        ],
      },
      {
        h2: "Installation et conditions de sécurité",
        paragraphs: [
          "L'installation repose sur trois règles : bouteilles arrimées verticalement hors de portée du public, flexibles protégés au sol, et zone de tir dégagée sur au moins deux mètres au-dessus et devant le canon. Les canons sont toujours orientés vers le haut, jamais vers les invités.",
          "La salle doit être ventilée. Nous vérifions systématiquement le volume, la hauteur sous plafond et la présence d'aération avec le responsable du lieu. Dans un espace clos et bas de plafond, le nombre de tirs est réduit ou l'effet remplacé par une alternative.",
          "Le matériel est contrôlé avant chaque prestation, et DJ Val dispose d'une assurance responsabilité civile professionnelle couvrant l'usage des effets spéciaux.",
        ],
      },
      {
        h2: "Sur quels événements l'utiliser",
        paragraphs: [
          "Le canon CO₂ est particulièrement adapté aux soirées d'entreprise, aux anniversaires festifs, aux soirées de club et de bar, ainsi qu'aux fins de soirée de mariage. Sur un mariage, il est souvent réservé au moment où le dancefloor est plein, après le gâteau.",
          "Il se combine bien avec les lyres LED et les lasers : le panache blanc réfléchit la lumière et rend les faisceaux visibles, ce qui décuple l'effet visuel. En revanche, il est inutile de l'associer à une machine à fumée lourde, qui occupe déjà l'espace au sol.",
          "Le canon CO₂ est également disponible en location seule, avec bouteille et flexible, pour les organisateurs équipés d'un technicien.",
        ],
      },
    ],
    equipment: { slug: "canon-co2", label: "Location de canon à CO2" },
    faq: [
      {
        q: "Le canon CO₂ est-il dangereux pour les invités ?",
        a: "Non, dès lors que les règles d'installation sont respectées : tir orienté vers le haut, zone dégagée, bouteilles arrimées et salle ventilée. Ces conditions sont vérifiées avant chaque prestation.",
      },
      {
        q: "L'effet mouille-t-il la piste de danse ?",
        a: "Non. Le CO₂ passe directement de l'état comprimé à l'état gazeux : il n'y a ni liquide ni condensation au sol, contrairement à certaines machines à fumée.",
      },
      {
        q: "Combien de tirs peut-on faire avec une bouteille ?",
        a: "Une bouteille standard permet environ 15 à 25 tirs courts selon la pression et la durée de chaque jet. Une bouteille de secours est prévue sur les prestations longues.",
      },
      {
        q: "Peut-on utiliser un canon CO₂ dans une salle des fêtes communale ?",
        a: "Souvent oui, mais l'accord du gestionnaire est indispensable et la ventilation doit être suffisante. Nous faisons cette démarche avec vous avant de confirmer l'effet au devis.",
      },
      {
        q: "Quel est le prix d'une animation canon CO₂ en Vaucluse ?",
        a: "L'effet est proposé en option d'une prestation DJ, bouteille et pilotage inclus. Le tarif dépend du nombre de canons et de bouteilles. Devis gratuit sous 24 h au 07 67 90 86 67.",
      },
    ],
  },
};

export const THEME_PAGE_SLUGS = Object.keys(THEME_PAGES);
